// -----------------------------------------------------------------------------
// CompletedTreatmentHub
//
// The COMPLETED-TREATMENT "wrap-up hub". Replaces the old confirm-only
// CustomCompleteTreatmentDialog while staying 100% backward-compatible:
//
//   • row PRESENT  → rich wrap-up experience:
//                      TreatmentContextHeader  (treatment context, read-only)
//                    + CompletionChecklist     (open TREATMENT_STARTED items)
//                    + ActionPanel             (shown when a checklist item is clicked)
//                    + footer: Cancel + "Complete treatment"
//   • row ABSENT   → legacy warning-box + Cancel/Finish layout, so the 4 callers
//                    that pass no row keep working unchanged.
//
// Reuses the original full-screen Dialog + Slide + sticky AppBar + fixed
// bottom-bar scaffold. The completion action (handleYes) and its API body are
// unchanged — this component never touches the CompleteTreatment thunk.
//
// Row shape: the TREATMENT_STARTED grid rows are produced by parseV2ToV1, which
// spreads lead fields to the top level and nests the treatment under
// row.lead_treatments[0] (treatment_data = the v2 treatment object). leadId and
// a minimal leadDetail are reconstructed here so the checklist hook + panels
// resolve correctly. See the treatment_data investigation note in the PR.
// -----------------------------------------------------------------------------
import React, { forwardRef, useMemo, useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { AppBar, Box, Button, Container, Dialog, DialogContent, DialogActions, IconButton, Slide, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import NotesTab from 'src/sections/@dashboard/lead/NotesTab';
import CallLogs from 'src/sections/@dashboard/leadDetail/CallLogs';
import IosEmailComposerBody from 'src/sections/tasks/composers/IosEmailComposerBody';
import FollowUpWhatsappChat from 'src/components/whatsappComponents/FollowUpWhatsappChat';
import { dispatch, useSelector } from 'src/redux/store';
import { callApi } from 'src/utils/apiCall';
import { hasFeatureAccess } from 'src/utils/featurePermission';
import { completeChecklistItem, reopenChecklistItem } from 'src/redux/slices/taskSlice';
import { updateError } from 'src/redux/slices/user';
import { followUpSlice } from 'src/redux/slices/lead';
import cascadeTaskDueDates from 'src/utils/cascadeTaskDueDates';
import { CHECKLIST_COMPLETION_STATE } from 'src/constants/tasksV2';
import ActionPanel from 'src/sections/@dashboard/leadDetail/AttendedGuidedChecklist/ActionPanel';
import RescheduleDialog from 'src/sections/@dashboard/leadDetail/AttendedGuidedChecklist/v2/RescheduleDialog';
import { TaskAssigneeHeads } from 'src/sections/@dashboard/leadDetail/AttendedGuidedChecklist/v2/UpNextCardV2';
import OverviewTab from 'src/sections/@dashboard/lead/OverviewTab';
import AttendedFullscreenWrapper from 'src/components/fullScreenPopUps/AttendedFullscreenWrapper';
import CompleteTreatmentConfirmDialog from 'src/components/Attended/CompleteTreatmentConfirmDialog';
import {
    commonDialogHeaderStyle,
    reminderTitleStyle,
    cancelBoxStyle,
    reminderDialogStyle,
} from 'src/styles/ScheduleReminder';
import { parseApiDate } from 'src/utils/taskDateUtils';
import CompletionChecklist from './CompletionChecklist';
import useCompletionChecklist from './useCompletionChecklist';
import useResumeGuard from './useResumeGuard';
import HubSidebarDetails from './HubSidebarDetails';

const Transition = forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

// Resolve the numeric lead id from a (possibly composite) flat-row id.
function resolveLeadId(row) {
    if (!row) return null;
    if (row.lead_id != null && row.lead_id !== 0) return row.lead_id;
    // parseV2ToV1 rows have id = `${leadId}-${index}`.
    const idStr = String(row.id ?? '');
    const leadPart = idStr.split('-')[0];
    return leadPart ? Number(leadPart) : null;
}

// Resolve the genuine lead-treatment object for the row the hub was opened on.
//
// PROBED on production (2026-05-28) against Carina (p18 lead 115648) and Raheem
// (p92 lead 153441 — a 2-treatment lead):
//   • V2-flat row → parseV2ToV1() → useLeadTableHelper.handleTableData().
//   • The REAL lead_treatment object lives at `row.treatmentData[0]`
//     (handleTableData sets `treatmentData: item.lead_treatments`, and the
//      parseV2ToV1 lead_treatments[0] is the spread v2 treatment object).
//   • Confirmed field paths on that object:
//       row.treatmentData[0].id                              → real lead_treatment id
//                                                              (Carina 113395, Raheem 135917/135918)
//       row.treatmentData[0].treatment_data.treatment_item.id → real treatment_item id
//                                                              (Carina 22, Raheem 48/22)
//       row.treatmentData[0].status                          → treatment status
//   • row.id is the MANGLED "${leadId}-${index}" composite — NEVER the lead_treatment id.
//
// The TreatmentStarted table is one row per treatment, so treatmentData[0] is
// THIS row's treatment. We therefore build a single-element lead_treatments[]
// from it (not a status search) so the hook scopes to exactly this treatment —
// critical for multi-treatment leads (Raheem's Invisalign checklist only
// resolves when the hub is opened on the Invisalign row).
// RCG-2: normalize a raw treatment_data candidate to object-or-null — a bare
// string (e.g. taskBrief-style rows carry `treatment: 'Invisalign'` as a
// string, not an object) must never leak through as treatment_data, because
// downstream `Object.keys(treatment_data)` would then return per-character
// digit keys ("0".."9") instead of [].
// RCA10: the serializer origin for `treatment_data` (object vs array) is
// still unconfirmed BE-side — this array-unwrap is defensive FE hardening so
// an array shape never leaks digit-keyed `Object.keys` or a missing
// `treatment_item.id`.
function normalizeTreatmentDataObject(candidate) {
    if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
        return candidate;
    }
    if (Array.isArray(candidate) && candidate.length > 0) {
        const firstObject = candidate.find((item) => item && typeof item === 'object' && !Array.isArray(item));
        return firstObject ?? null;
    }
    return null;
}

// Resolve a treatment NAME, preferring the normalized treatment_data object's
// treatment_item.name, then any string candidates in priority order (e.g. a
// sibling `tx.treatment` string field, then the raw pre-normalization
// candidate — taskBrief-style rows). Returns '' when nothing yields a name.
function resolveTreatmentName(normalizedObject, ...stringCandidates) {
    const fromObject = normalizedObject?.treatment_item?.name;
    if (fromObject) return fromObject;
    const fromString = stringCandidates.find((c) => typeof c === 'string');
    return fromString ?? '';
}

// RCA10: extract the ltId half of a parseV2ToV1 composite row id
// (`${leadId}-${ltId}`). Returns the numeric ltId when the id looks
// numeric, otherwise the raw string half, or null when there is no
// composite ('-') to split. Exported for unit coverage.
export function deriveClickedLtId(rowId) {
    const str = String(rowId ?? '');
    if (!str.includes('-')) return null;
    const ltPart = str.split('-')[1];
    if (ltPart == null || ltPart === '') return null;
    return /^-?\d+$/.test(ltPart) ? Number(ltPart) : ltPart;
}

export function resolveRowTreatment(row) {
    if (!row) return null;
    // Prefer the helper-transformed table shape, then the parsed flat shape.
    // Each carries the genuine ids. A multi-treatment row must resolve to the
    // CLICKED treatment (row.treatment_id, set by useLeadTableHelper's
    // handleTableData, or — on pre-helper rows — derived from the composite
    // row.id) — never blindly [0], which bled the wrong treatment on
    // multi-treatment leads (RCA10 / JAVASCRIPT-REACT-KK).
    let list = [];
    if (Array.isArray(row.treatmentData)) {
        list = row.treatmentData;
    } else if (Array.isArray(row.lead_treatments)) {
        list = row.lead_treatments;
    }
    const clickedLtId = (row.treatment_id != null && row.treatment_id !== '')
        ? row.treatment_id
        : deriveClickedLtId(row.id);
    const tx =
        (clickedLtId != null ? (list.find((t) => t && String(t.id) === String(clickedLtId)) ?? null) : null)
        ?? (list.length === 1 ? list[0] : null); // sole element is unambiguous; NEVER pick [0] from a multi-element list
    if (tx) {
        // treatment_data is where treatment_item.id lives; on raw rows it may be
        // under `treatment` instead. Guard to object-or-null (never a string).
        const rawTd = tx.treatment_data ?? tx.treatment ?? null;
        const treatmentData = normalizeTreatmentDataObject(rawTd);
        const name = resolveTreatmentName(treatmentData, tx.treatment, rawTd);
        return {
            id: tx.id ?? null,                 // genuine lead_treatment id
            status: tx.status ?? row.status ?? null,
            treatment_data: treatmentData,     // carries treatment_item.id (object-or-null)
            treatment: name,
        };
    }
    // Raw v2-flat fallback (treatment is an object with treatment_item).
    const rawTd = row.treatment_data ?? row.treatment ?? null;
    const treatmentData = normalizeTreatmentDataObject(rawTd);
    return {
        id: row.treatment_id ?? null,          // helper exposes the real id here too
        status: row.status ?? null,
        treatment_data: treatmentData,
        treatment: resolveTreatmentName(treatmentData, row.treatment, rawTd),
    };
}

// Reconstruct a minimal leadDetail object for the checklist hook + ActionPanel.
// lead_treatments[] drives the treatment filter in useCompletionChecklist (needs
// .id = real lead_treatment id, .treatment_data.treatment_item.id = real
// treatment_item id, .status) and the panels' treatmentId (lead_treatments[0].id).
function buildLeadDetail(row, leadId, treatmentOverride) {
    if (!row) return null;
    const lead = row.lead || row; // raw rows nest lead; parsed rows spread it.
    const treatment = treatmentOverride || resolveRowTreatment(row);
    return {
        id: leadId,
        first_name: lead?.first_name ?? row.first_name ?? '',
        last_name: lead?.last_name ?? row.last_name ?? '',
        email: lead?.lead_contact?.email ?? row.email ?? '',
        lead_contact: lead?.lead_contact ?? row.lead_contact ?? null,
        lead_treatments: treatment ? [treatment] : [],
    };
}

// A genuine lead_treatment id is a positive integer. The mangled row.id
// composite ("115648-0-0") is not — use this to decide if a supplemental GET
// is needed to recover the real ids.
export function hasCleanTreatmentIds(treatment) {
    if (!treatment) return false;
    const ltId = treatment.id;
    const tiId = treatment.treatment_data?.treatment_item?.id;
    const isCleanInt = (v) => v != null && Number.isInteger(Number(v)) && !String(v).includes('-');
    return isCleanInt(ltId) && isCleanInt(tiId);
}

// RCG-2 D3 fix: pick the supplemental-fetch list item that matches the
// OPENED ROW's treatment IDENTITY, never by status. The prior implementation
// used `list.find((t) => t.status === 'TREATMENT_STARTED') || list[0]`, which
// on a multi-started lead (2+ treatments simultaneously TREATMENT_STARTED)
// always resolved to whichever treatment happened to be first/status-matched
// in the list — NOT necessarily the treatment the hub was opened on. That
// cross-bled the wrong treatment's checklist into the hub.
//
// Matching priority, keyed on rowTreatment (the row the hub was opened on):
//   1. lead_treatment id (rowTreatment.id)
//   2. treatment_item id (rowTreatment.treatment_data.treatment_item.id)
//   3. treatment name (rowTreatment.treatment)
// If none match, return null — DEGRADE rather than silently show the wrong
// treatment (picking list[0] is exactly the bug being fixed).
export function selectSupplementalTreatment(list, rowTreatment) {
    if (!Array.isArray(list) || list.length === 0 || !rowTreatment) return null;

    if (rowTreatment.id != null) {
        const byId = list.find((t) => String(t.id) === String(rowTreatment.id));
        if (byId) return byId;
    }

    // Descriptor fallbacks are only trustworthy when they identify a UNIQUE row —
    // with non-unique descriptors, .find() would silently pick the first match
    // and render the wrong treatment. Use .filter() and accept only length === 1;
    // ambiguous (>1) or absent (0) degrades (fails closed) rather than guessing.
    const rowTreatmentItemId = rowTreatment.treatment_data?.treatment_item?.id;
    if (rowTreatmentItemId != null) {
        const byTreatmentItemId = list.filter((t) => {
            const td = t.treatment_data ?? t.treatment;
            return String(td?.treatment_item?.id) === String(rowTreatmentItemId);
        });
        if (byTreatmentItemId.length === 1) return byTreatmentItemId[0];
        if (byTreatmentItemId.length > 1) return null;
    }

    if (rowTreatment.treatment) {
        const byName = list.filter((t) => {
            const td = t.treatment_data ?? t.treatment;
            const tdObj = normalizeTreatmentDataObject(td);
            const name = resolveTreatmentName(tdObj, td);
            return name === rowTreatment.treatment;
        });
        if (byName.length === 1) return byName[0];
        return null;
    }

    return null;
}

// task #22: maps item_type → short label for the RecentlyCompletedCard ack.
// Mirrors AttendedGuidedChecklistV2.jsx COMPLETION_LABELS and FollowUpV2.jsx:781-787.
const COMPLETION_LABELS = {
    WHATSAPP_SENT: 'WhatsApp sent',
    EMAIL_SENT: 'Email sent',
    LOG_PHONE_CALL: 'Call logged',
    LEAD_NEXT_ACTION: 'Next action set',
};

const CompletedTreatmentHub = ({ isLoader, open, handleClose, handleYes, row, practiceId, onDataChanged }) => {
    const [actionPanelItem, setActionPanelItem] = useState(null);
    // Populated only by the supplemental-GET fallback when the row lacks clean ids.
    const [fetchedTreatment, setFetchedTreatment] = useState(null);
    // Pause dialog state — mirrors AttendedGuidedChecklistV2 (date-picker for paused_until).
    const [pauseDialogItem, setPauseDialogItem] = useState(null);
    const [pauseUntilDate, setPauseUntilDate] = useState('');
    // task #22: sequenced completion ack — mirrors FollowUpV2.jsx:410-411 + AGCv2.jsx:187-188.
    // Set on successful checklist-item completion; auto-clears after 4s.
    // During the ack window CompletionChecklist shows RecentlyCompletedCard alone;
    // after the ack clears the reschedule banner + UpNextCardV2 appear together.
    const [recentlyCompleted, setRecentlyCompleted] = useState(null);
    const recentlyCompletedTimerRef = useRef(null);
    // Reschedule banner — armed on completion, shown AFTER ack clears.
    const [showRescheduleBanner, setShowRescheduleBanner] = useState(false);
    const [bannerRescheduleOpen, setBannerRescheduleOpen] = useState(false);
    // "Complete treatment" confirmation — treatment-close is irreversible (POST
    // treatment_closed), so the footer button opens this confirm before calling
    // handleYes (same guard the attended/consultation popups use).
    const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false);
    // Right-sidebar tab state — shared with AttendedFullscreenWrapper. 'overview' is
    // the resting tab (treatment summary); notes/email/whatsapp/debug mirror the
    // attended + consultation popups. email/whatsapp are opened programmatically by
    // the shared ActionPanel / FollowUpPanel via onOpenSidebarTab. sidebarPulse
    // re-keys the attention ring on each programmatic open.
    const [sidebarTab, setSidebarTab] = useState('overview');
    const [sidebarPulse, setSidebarPulse] = useState(0);

    const { user: authUser } = useAuth();
    const isSuperuser = !!authUser?.is_superuser;

    const leadId = useMemo(() => resolveLeadId(row), [row]);
    // The treatment resolved straight off the row (the cheap, preferred path).
    const rowTreatment = useMemo(() => resolveRowTreatment(row), [row]);
    // followLead primes the WhatsApp composer (state.lead.followLead), exactly as
    // OpenConsultationPopup does. Read it for the sidebar.
    const followLead = useSelector((s) => s.lead.followLead);
    const { currentTreatmentDataGlobal } = useSelector((s) => s.multiTreatment);
    const leadDetail = useMemo(
        () => buildLeadDetail(row, leadId, fetchedTreatment || rowTreatment),
        [row, leadId, fetchedTreatment, rowTreatment]
    );

    const checklistEnabled = !!row && hasFeatureAccess('tasks-v2', practiceId);

    // Supplemental fallback: if the row did NOT carry clean lead_treatment /
    // treatment_item ids, fetch the lead's treatments and pick the one matching
    // the opened row's treatment IDENTITY (see selectSupplementalTreatment — id,
    // then treatment_item id, then name; never status). Probing showed the table
    // rows DO carry clean ids, so this is a safety net, not the norm.
    useEffect(() => {
        setFetchedTreatment(null);
        if (!open || !checklistEnabled || !practiceId || !leadId) return undefined;
        if (hasCleanTreatmentIds(rowTreatment)) return undefined;

        let cancelled = false;
        (async () => {
            try {
                const res = await callApi(
                    `/api/v1/practices/${practiceId}/leads/${leadId}/treatments/`
                );
                let list = [];
                if (Array.isArray(res?.data)) list = res.data;
                else if (Array.isArray(res?.data?.results)) list = res.data.results;
                if (cancelled || list.length === 0) return;
                // RCG-2 D3: key on the opened row's treatment IDENTITY, not status —
                // see selectSupplementalTreatment for why a status-first find() bleeds
                // the wrong treatment on multi-started leads. Degrade (no update) on
                // no-match rather than falling back to list[0].
                const active = selectSupplementalTreatment(list, rowTreatment);
                if (!active) return;
                const rawTd = active.treatment_data ?? active.treatment ?? null;
                const treatmentData = normalizeTreatmentDataObject(rawTd);
                setFetchedTreatment({
                    id: active.id ?? null,
                    status: active.status ?? null,
                    treatment_data: treatmentData,
                    treatment: resolveTreatmentName(treatmentData, rawTd),
                });
            } catch (err) {
                Sentry.captureException(err, { tags: { context: 'completion_hub_treatment_fallback' } });
            }
        })();
        return () => { cancelled = true; };
    }, [open, checklistEnabled, practiceId, leadId, rowTreatment]);

    // Prime state.lead.followLead (used by FollowUpWhatsappChat) on open — same
    // call OpenConsultationPopup makes. Reset sidebar to the overview tab on open.
    useEffect(() => {
        if (!open || !leadId || !practiceId) return;
        setSidebarTab('overview');
        dispatch(followUpSlice(leadId, practiceId, 'LeadDetail'));
    }, [open, leadId, practiceId]);

    // Reset the pulse counter when the popup closes so it doesn't replay on reopen.
    useEffect(() => {
        if (!open) setSidebarPulse(0);
    }, [open]);

    // Programmatic sidebar switch — passed to ActionPanel/FollowUpPanel. Switching
    // to email/whatsapp replays the attention pulse so the composer draws the eye.
    // The shared ActionPanel resets to 'overview' when an item is switched.
    const onOpenSidebarTab = useCallback((v) => {
        setSidebarTab(v);
        setSidebarPulse((n) => n + 1);
    }, []);

    const handleTabChange = useCallback((event, newValue) => setSidebarTab(newValue), []);

    // Shared OverviewTab inputs. The timeline lists the lead's treatments (same
    // GET FollowUpV2 / OpenAttendedPopup use); `user.lead_id` is what its
    // mark-complete refresh reads.
    const [overviewTreatments, setOverviewTreatments] = useState([]);
    useEffect(() => {
        if (!open || !practiceId || !leadId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const resp = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/treatments`, 'GET');
                if (!cancelled) setOverviewTreatments(resp?.data?.results ?? []);
            } catch (error) {
                console.error('Error fetching treatment data:', error);
            }
        })();
        return () => { cancelled = true; };
    }, [open, practiceId, leadId]);
    const overviewUser = useMemo(() => (leadDetail ? { ...leadDetail, lead_id: leadId } : null), [leadDetail, leadId]);

    // Own the checklist hook at the hub level so we can refresh() after a panel
    // completion or an undo — mirrors AttendedGuidedChecklist, which calls the
    // hook's refresh() after every complete/undo so the list reconciles in place.
    // groups/loading/templatesEmpty are passed DOWN to CompletionChecklist as
    // props (it no longer calls the hook itself).
    const {
        initialLoading: checklistLoading,
        templatesEmpty: checklistEmpty,
        groups: checklistGroups,
        upNext: checklistUpNext,
        counts: checklistCounts,
        hiddenTaskTemplate,
        refresh: refreshChecklist,
    } = useCompletionChecklist({ practiceId, leadId, leadDetail, taskId: row?.task_id ?? null });

    const handleItemClick = useCallback((item) => {
        if (item?._preview) return; // preview items are non-interactive
        setActionPanelItem(item);
    }, []);

    // Panel CANCEL (no completion): clear the panel only — do NOT clobber a
    // user-selected sidebar tab (Notes, Email, etc.). Mirrors the V2 attended
    // popup handlePanelClose which only calls setActionPanelItem(null).
    const handlePanelClose = useCallback(() => {
        setActionPanelItem(null);
        if (checklistEnabled) refreshChecklist({ bustCache: true });
    }, [checklistEnabled, refreshChecklist]);

    const applyPanelCompleteSuccess = useCallback((completedItem) => {
        const label = COMPLETION_LABELS[completedItem?.item_type] || 'Task completed';
        if (recentlyCompletedTimerRef.current) clearTimeout(recentlyCompletedTimerRef.current);
        setRecentlyCompleted({ completed: completedItem, label, completedAt: new Date() });
        recentlyCompletedTimerRef.current = setTimeout(() => setRecentlyCompleted(null), 4000);
        setShowRescheduleBanner(true);
    }, []);

    // Panel COMPLETE: a panel's own action button (e.g. "Save", "Send link",
    // "Mark follow-up done") calls onComplete(item). The hub must actually mark
    // the checklist item done on the BE — otherwise the panel looks like a dead
    // box (it accepts input but nothing persists / the item never moves to Done).
    // Mirrors AttendedGuidedChecklistV2.handlePanelComplete: POST /complete/ with
    // the canonical body, forwarding resolved_action when a panel attached it
    // (LEAD_NEXT_ACTION / OPEN_FOLLOW_UP require it). Then close + cache-bust.
    const handlePanelComplete = useCallback((completedItem) => {
        setActionPanelItem(null);
        setSidebarTab('overview');
        const taskId = completedItem?.taskId ?? completedItem?.rawTask?.id;
        const itemId = completedItem?.itemId ?? completedItem?.id;
        if (!checklistEnabled || !practiceId || !taskId || !itemId) {
            if (checklistEnabled) refreshChecklist({ bustCache: true });
            return;
        }
        if (completedItem?.completionAlreadyPersisted) {
            refreshChecklist({ bustCache: true });
            applyPanelCompleteSuccess(completedItem);
            return;
        }
        // Guided follow-up sequence (FollowUpPanel) attaches channelOutcomes —
        // persist it as response_payload.channels alongside any resolved_action.
        let payload = completedItem?.resolvedAction
            ? { resolved_action: completedItem.resolvedAction }
            : null;
        if (Array.isArray(completedItem?.channelOutcomes) && completedItem.channelOutcomes.length > 0) {
            payload = { ...(payload || {}), channels: completedItem.channelOutcomes };
        }
        dispatch(completeChecklistItem(
            practiceId, taskId, itemId,
            ({ success }) => {
                refreshChecklist({ bustCache: true });
                // task #22: arm the sequenced ack window on success — mirrors FollowUpV2:778-796.
                // refreshChecklist fires first so the data updates while the 4s ack is visible.
                if (success) {
                    applyPanelCompleteSuccess(completedItem);
                }
            },
            payload,
        ));
    }, [applyPanelCompleteSuccess, checklistEnabled, practiceId, refreshChecklist]);

    // task #22: direct card-level mark-complete (UpNextCardV2 onMarkComplete prop).
    // Mirrors FollowUpV2.jsx:767-802 — fires the same completeChecklistItem thunk,
    // then arms the 4s ack window. No resolvedAction path (direct completion only).
    const handleCardMarkComplete = useCallback((item) => {
        const taskId = item?.taskId ?? item?.rawTask?.id;
        const itemId = item?.itemId ?? item?.id;
        if (!checklistEnabled || !practiceId || !taskId || !itemId) return;
        dispatch(completeChecklistItem(
            practiceId, taskId, itemId,
            ({ success }) => {
                refreshChecklist({ bustCache: true });
                if (success) {
                    const label = COMPLETION_LABELS[item?.item_type] || 'Task completed';
                    if (recentlyCompletedTimerRef.current) clearTimeout(recentlyCompletedTimerRef.current);
                    setRecentlyCompleted({ completed: item, label, completedAt: new Date() });
                    recentlyCompletedTimerRef.current = setTimeout(() => setRecentlyCompleted(null), 4000);
                    setShowRescheduleBanner(true);
                }
            },
        ));
    }, [checklistEnabled, practiceId, refreshChecklist]);

    // Reopen a completed/skipped/paused checklist item (mirrors the attended grid),
    // then refresh so the item moves back out of Done in place. Uses the same
    // reopenChecklistItem thunk as AttendedGuidedChecklistV2 so it gets the tasks-v2
    // gate, the success/error snackbar (failures are never silent) and
    // emitTasksV2Changed (Overview summary / task lists refresh). Resolves only after
    // the reopen call has settled and the refresh has been kicked off.
    const handleUndo = useCallback(async (item) => {
        const taskId = item?.taskId ?? item?.rawTask?.id;
        const itemId = item?.itemId ?? item?.id;
        if (!practiceId || !taskId || !itemId) return;
        try {
            await dispatch(reopenChecklistItem(practiceId, taskId, itemId));
        } catch (err) {
            Sentry.captureException(err, { tags: { context: 'completion_hub_undo' } });
            dispatch(updateError({ success: false, message: err?.message || 'Something went wrong' }));
        } finally {
            if (checklistEnabled) await refreshChecklist({ bustCache: true });
        }
    }, [practiceId, checklistEnabled, refreshChecklist]);

    // Resume from the paused card: handleUndo behind an in-flight guard (see useResumeGuard).
    const [resuming, handleResumeChecklist] = useResumeGuard(handleUndo);

    // Undo from the green completion ack card — same behaviour as FollowUpV2 /
    // AttendedGuidedChecklistV2: cancel the auto-dismiss timer, reopen the
    // just-completed item, clear the ack.
    const handleAckUndo = useCallback(() => {
        if (recentlyCompletedTimerRef.current) clearTimeout(recentlyCompletedTimerRef.current);
        if (recentlyCompleted?.completed) handleUndo(recentlyCompleted.completed);
        setRecentlyCompleted(null);
    }, [recentlyCompleted, handleUndo]);

    // SKIP — mirrors AttendedGuidedChecklistV2.handleSkip: same /complete/ thunk
    // with CHECKLIST_COMPLETION_STATE.SKIPPED. Then cache-bust refresh.
    const handleSkip = useCallback((skippedItem) => {
        const taskId = skippedItem?.taskId ?? skippedItem?.rawTask?.id;
        const itemId = skippedItem?.itemId ?? skippedItem?.id;
        if (!checklistEnabled || !practiceId || !taskId || !itemId) return;
        dispatch(completeChecklistItem(
            practiceId, taskId, itemId,
            ({ success }) => { if (success) refreshChecklist({ bustCache: true }); },
            { source: 'completion_hub_skip' },
            CHECKLIST_COMPLETION_STATE.SKIPPED,
        ));
    }, [checklistEnabled, practiceId, refreshChecklist]);

    // PAUSE (CHECKLIST-WIDE) — the /pause/ endpoint pauses the WHOLE task-group,
    // not just one item (confirmed at runtime), so the hub surfaces it as a single
    // checklist-level "Pause checklist" control rather than a per-item action.
    // Opens a dialog pre-filled to 7 days from now at 09:00 (local) and uses any
    // active item as the handle into its task-group. Same endpoint as attended.
    const handlePauseChecklist = useCallback(() => {
        const { today = [], later = [] } = checklistGroups || {};
        const activeItem = [...today, ...later].find((i) => i?.taskId && i?.itemId)
            || [...today, ...later][0]
            || null;
        if (!activeItem) return;
        const d = new Date();
        d.setDate(d.getDate() + 7);
        const pad = (n) => String(n).padStart(2, '0');
        const localStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T09:00`;
        setPauseUntilDate(localStr);
        setPauseDialogItem(activeItem);
    }, [checklistGroups]);

    const handlePauseConfirm = useCallback(async () => {
        const item = pauseDialogItem;
        const taskId = item?.taskId ?? item?.rawTask?.id;
        const itemId = item?.itemId ?? item?.id;
        if (!taskId || !itemId || !pauseUntilDate) return;
        setPauseDialogItem(null);
        // Same endpoint + body as AttendedGuidedChecklistV2.handlePauseConfirm —
        // pauses the whole checklist/task-group until the chosen date.
        const resp = await callApi(
            `/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${itemId}/pause/`,
            'post',
            { paused_until: new Date(pauseUntilDate).toISOString() }
        );
        if (resp.ok) {
            dispatch(updateError({ success: true, message: 'Checklist paused' }));
            refreshChecklist({ bustCache: true });
        } else {
            dispatch(updateError({ success: false, message: resp?.data?.detail || 'Failed to pause checklist' }));
        }
    }, [pauseDialogItem, pauseUntilDate, practiceId, refreshChecklist]);

    // CHANGE DUE DATE — mirrors AttendedGuidedChecklistV2.handleChangeDueDate:
    // cascade the delta to all incomplete dated tasks, PATCH each task's due_at,
    // then refresh. (Preview items have no taskId — spawn from template first.)
    const handleChangeDueDate = useCallback(async (item, newDate) => {
        if (!item?.taskId) {
            if (!item?._templateId) {
                dispatch(updateError({ success: false, message: "Due dates can't be set on this item yet." }));
                return;
            }
            const spawnResp = await callApi(
                `/api/v1/practices/${practiceId}/tasks/from-template/`,
                'post',
                { template_id: item._templateId, lead_id: leadId }
            );
            if (!spawnResp.ok && spawnResp.status !== 200 && spawnResp.status !== 201) {
                dispatch(updateError({ success: false, message: "Couldn't create tasks. Please try again." }));
                return;
            }
            const newTaskId = spawnResp.data?.id;
            if (newTaskId) {
                await callApi(`/api/v1/practices/${practiceId}/tasks/${newTaskId}/`, 'patch', { due_at: newDate.toISOString() });
                dispatch(updateError({ success: true, message: 'Tasks created and due date set.' }));
            } else {
                dispatch(updateError({ success: true, message: 'Tasks created! Select a task to set its due date.' }));
            }
            refreshChecklist({ bustCache: true });
            return;
        }
        const { today = [], later = [], done = [] } = checklistGroups || {};
        const allItems = [...today, ...later, ...done];
        const cascade = cascadeTaskDueDates({
            items: allItems,
            anchorTaskId: item.taskId,
            oldDueAt: item.due_at,
            newDueAt: newDate,
        });

        const results = await Promise.allSettled(
            cascade.map(({ taskId, newDueAt: d }) =>
                callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/`, 'patch', { due_at: d.toISOString() })
            )
        );

        const anyFailed = results.some((r) => r.status === 'rejected' || !r.value?.ok);
        if (anyFailed) {
            dispatch(updateError({ success: false, message: "Couldn't update due dates. Please try again." }));
            return;
        }

        const nShifted = cascade.length;
        const oldMs = item.due_at ? new Date(parseApiDate(item.due_at)).getTime() : Date.now();
        const deltaDays = Math.round(Math.abs(newDate.getTime() - oldMs) / (24 * 60 * 60 * 1000));
        dispatch(updateError({
            success: true,
            message: `Due dates updated — ${nShifted} task${nShifted !== 1 ? 's' : ''} shifted by ${deltaDays} day${deltaDays !== 1 ? 's' : ''}`,
        }));
        refreshChecklist({ bustCache: true });
    }, [practiceId, leadId, checklistGroups, refreshChecklist]);

    // Progress summary for the main column header (only when the checklist is
    // enabled, loaded and non-empty). Done = genuinely completed only; skipped and
    // paused are counted separately. progress = done / every scoped item.
    const progressTotal = checklistCounts.total;
    const progressDone = checklistCounts.done;
    const showProgress = checklistEnabled && !checklistLoading && !checklistEmpty && progressTotal > 0;

    // -----------------------------------------------------------------------
    // Right-sidebar tabs — same framework + tab set as the attended / consultation
    // popups (AttendedFullscreenWrapper). Overview hosts the treatment summary +
    // change-start-date + edit-patient (the hub's contextual info — NOT outcome
    // actions). Notes / Email / WhatsApp are shared comms chrome. Debug is
    // superadmin-only, matching OpenConsultationPopup.
    // -----------------------------------------------------------------------
    const SIDEBAR_TABS = [
        {
            icon: 'grommet-icons:overview',
            label: 'Overview',
            value: 'overview',
            component: (
                <OverviewTab
                    treatmentTabData={overviewTreatments}
                    leadId={leadId}
                    handleChange={handleTabChange}
                    user={overviewUser}
                    LeadNotes={[]}
                    showDetailModes
                    topSlot={(
                        <HubSidebarDetails
                            row={row}
                            leadId={leadId}
                            practiceId={practiceId}
                            treatmentId={rowTreatment?.id}
                            treatmentStatus={rowTreatment?.status}
                            handleClose={handleClose}
                            onUpdated={onDataChanged}
                        />
                    )}
                />
            ),
            width: 24,
            height: 24,
        },
        {
            icon: 'solar:chat-round-dots-bold',
            label: 'Notes',
            value: 'notes',
            component: (
                <NotesTab
                    LeadNotes={[]}
                    isForTab
                    userDetails={leadDetail}
                    handleChange={handleTabChange}
                />
            ),
            width: 20,
            height: 20,
        },
        {
            icon: 'solar:phone-calling-rounded-bold',
            label: 'Calls',
            value: 'calls',
            component: (
                <CallLogs leadDetail={leadDetail} />
            ),
            width: 20,
            height: 20,
        },
        {
            icon: 'mdi:email-outline',
            label: 'Email',
            value: 'email',
            component: (
                <IosEmailComposerBody
                    leadId={leadId}
                    practiceId={practiceId}
                    leadDetail={leadDetail}
                    compact
                    onSendSuccess={() => onOpenSidebarTab('overview')}
                />
            ),
            width: 20,
            height: 20,
        },
        {
            icon: 'ic:baseline-whatsapp',
            label: 'WhatsApp',
            value: 'whatsapp',
            component: (
                <FollowUpWhatsappChat
                    user={leadDetail}
                    followLead={followLead}
                />
            ),
            width: 20,
            height: 20,
        },
        ...(isSuperuser ? [{
            icon: 'solar:bug-bold-duotone',
            label: 'Debug',
            value: 'debug',
            component: (
                <Box sx={{ p: 2 }}>
                    <Typography sx={{ fontSize: '11px', color: '#6e6e73', letterSpacing: '0.02em', textTransform: 'uppercase', fontWeight: 600, mb: 1.5 }}>
                        Completion hub (superadmin)
                    </Typography>
                    <Box sx={{ fontFamily: 'monospace', fontSize: '11px', color: '#1d1d1f', bgcolor: '#F5F5F7', p: 1.5, borderRadius: '8px', lineHeight: 1.7 }}>
                        <div>practiceId: {String(practiceId)}</div>
                        <div>leadId: {String(leadId)}</div>
                        <div>treatmentId: {String(rowTreatment?.id)}</div>
                        <div>treatmentStatus: {String(rowTreatment?.status)}</div>
                        <div>checklistEnabled: {String(checklistEnabled)}</div>
                        <div>checklistLoading: {String(checklistLoading)}</div>
                        <div>checklistEmpty: {String(checklistEmpty)}</div>
                        <div>progress: {progressDone} / {progressTotal}</div>
                        <div>usedFallbackGet: {String(!!fetchedTreatment)}</div>
                    </Box>
                </Box>
            ),
            width: 18,
            height: 18,
        }] : []),
    ];

    // -----------------------------------------------------------------------
    // Hub footer (row path) — fixed bottom action bar. "Done" is the everyday
    // exit (no status change); "Complete treatment" is the rare destructive close
    // (STARTED → CLOSED, can't be undone) de-emphasised to a quiet text link.
    // Rendered inside the wrapper's left content (children) — position:fixed pins
    // it to the viewport bottom across the full width.
    // -----------------------------------------------------------------------
    const hubFooter = (
        <Box
            sx={{
                width: '100%',
                position: 'fixed',
                left: 0,
                bottom: 0,
                pb: 1.5,
                pt: 1.5,
                bgcolor: 'rgba(246, 246, 248, 0.82)',
                backdropFilter: 'saturate(180%) blur(20px)',
                WebkitBackdropFilter: 'saturate(180%) blur(20px)',
                borderTop: '0.5px solid rgba(0, 0, 0, 0.10)',
                zIndex: 99,
            }}
        >
            <Container maxWidth="xl" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
                <LoadingButton
                    data-testid="hub-done-btn"
                    variant="contained"
                    loading={isLoader}
                    onClick={handleClose}
                    disabled={isLoader}
                    sx={{
                        bgcolor: '#00AB55',
                        color: '#ffffff',
                        fontSize: '14px',
                        fontWeight: 700,
                        letterSpacing: '-0.01em',
                        textTransform: 'none',
                        borderRadius: '8px',
                        boxShadow: 'none',
                        px: 4,
                        py: 1,
                        minWidth: '140px',
                        '&:hover': { bgcolor: '#02934A', boxShadow: 'none' },
                        '&:active': { bgcolor: '#017A3D' },
                        '&.Mui-disabled': { bgcolor: 'rgba(0,171,85,0.35)', color: '#ffffff' },
                    }}
                >
                    Done
                </LoadingButton>

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                    <LoadingButton
                        data-testid="hub-complete-treatment-btn"
                        variant="text"
                        loading={isLoader}
                        onClick={() => setCompleteConfirmOpen(true)}
                        disabled={isLoader}
                        sx={{
                            color: '#86868b',
                            fontSize: '12px',
                            fontWeight: 500,
                            letterSpacing: '-0.005em',
                            textTransform: 'none',
                            borderRadius: '6px',
                            boxShadow: 'none',
                            px: 1.25,
                            py: 0.5,
                            minWidth: 0,
                            '&:hover': { bgcolor: 'rgba(209,67,67,0.06)', color: '#D14343' },
                            '&.Mui-disabled': { color: 'rgba(134,134,139,0.5)' },
                        }}
                    >
                        Complete treatment
                    </LoadingButton>
                    <Typography sx={{ fontSize: '10px', color: '#aeaeb2', letterSpacing: '-0.005em', lineHeight: 1.3 }}>
                        Ends &amp; closes the treatment · can&apos;t be undone
                    </Typography>
                </Box>
            </Container>
        </Box>
    );

    // Pause dialog — checklist-wide pause (the /pause/ endpoint pauses the whole
    // task-group). Date picker for paused_until. Rendered alongside the row path.
    const pauseDialog = (
        <Dialog
            data-testid="hub-pause-dialog"
            open={pauseDialogItem != null}
            onClose={() => setPauseDialogItem(null)}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: '16px' } }}
        >
            <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6e6e73', mb: 0.5 }}>
                    Pause checklist
                </Typography>
                <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.02em' }}>
                    Pause this checklist
                </Typography>
            </Box>
            <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
                <Typography sx={{ fontSize: '13px', color: '#3c3c43', mb: 1.5, letterSpacing: '-0.005em' }}>
                    Pauses the whole checklist until the chosen date. Resume on:
                </Typography>
                <input
                    data-testid="hub-pause-date"
                    type="datetime-local"
                    value={pauseUntilDate}
                    onChange={(e) => setPauseUntilDate(e.target.value)}
                    style={{
                        width: '100%',
                        padding: '10px 12px',
                        fontSize: '14px',
                        borderRadius: '8px',
                        border: '1.5px solid rgba(0,0,0,0.15)',
                        fontFamily: 'inherit',
                        color: '#1d1d1f',
                        outline: 'none',
                        boxSizing: 'border-box',
                    }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.75, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                <Button
                    onClick={() => setPauseDialogItem(null)}
                    sx={{ color: '#6e6e73', textTransform: 'none', fontSize: '14px', fontWeight: 500 }}
                >
                    Cancel
                </Button>
                <Button
                    data-testid="hub-pause-confirm"
                    variant="contained"
                    disableElevation
                    disabled={!pauseUntilDate}
                    onClick={handlePauseConfirm}
                    sx={{
                        bgcolor: '#078DEE',
                        color: '#fff',
                        textTransform: 'none',
                        fontSize: '14px',
                        fontWeight: 600,
                        py: 0.75,
                        px: 2.5,
                        borderRadius: '8px',
                        '&:hover': { bgcolor: '#0670c4' },
                    }}
                >
                    Pause checklist
                </Button>
            </DialogActions>
        </Dialog>
    );

    // ROW PATH — the full wrap-up hub, hosted in the shared AttendedFullscreenWrapper
    // so its chrome (header / equal-height columns / right-sidebar tab framework /
    // pulse) is identical to the attended + consultation popups.
    if (row) {
        // Name the treatment this hub is for (multi-treatment leads).
        const hubTitle = typeof rowTreatment?.treatment === 'string' && rowTreatment.treatment
            ? `Treatment in progress · ${rowTreatment.treatment}`
            : 'Treatment in progress';
        return (
            <>
                <AttendedFullscreenWrapper
                    open={open}
                    onClose={handleClose}
                    headerButtons
                    headerTitle={hubTitle}
                    sidebarTabs={SIDEBAR_TABS}
                    sidebarTab={sidebarTab}
                    onTabChange={handleTabChange}
                    sidebarPulseKey={sidebarPulse}
                    pulseCssName="hubPulse"
                >
                    <Box sx={{ pb: 12 }}>
                        {/* task #22: reschedule banner — armed after a checklist-item completion
                            (showRescheduleBanner=true), rendered AFTER the 4s ack clears
                            (!recentlyCompleted gate). Mirrors FollowUpV2.jsx:2076-2134 and
                            AGCv2.jsx:1297-1377 exactly (same markup, same logic). */}
                        {checklistEnabled && !recentlyCompleted && showRescheduleBanner && checklistUpNext && (
                            <Box
                                data-testid="reschedule-banner"
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1,
                                    px: 2, py: 1.25, mx: 3, mb: 0,
                                    bgcolor: 'rgba(7,141,238,0.06)',
                                    border: '1px solid rgba(7,141,238,0.18)',
                                    borderRadius: '10px',
                                    flexWrap: 'wrap', rowGap: '8px',
                                }}
                            >
                                <Iconify icon="mdi:calendar-clock" width={16} height={16} sx={{ color: '#078DEE', flexShrink: 0 }} />
                                <Typography sx={{ flex: 1, fontSize: '12px', color: '#3c3c43', letterSpacing: '-0.005em', minWidth: 0 }}>
                                    Would you like to reschedule or assign your next task?
                                </Typography>
                                {checklistUpNext?.taskId && practiceId && (
                                    <Box data-testid="reschedule-banner-assignee" sx={{ flexShrink: 0 }}>
                                        <TaskAssigneeHeads
                                            practiceId={practiceId}
                                            taskId={checklistUpNext.taskId}
                                            initialAssignees={checklistUpNext.rawTask?.assignees || []}
                                            leadId={leadId}
                                            onLeadTasksReassigned={() => refreshChecklist({ bustCache: true })}
                                        />
                                    </Box>
                                )}
                                <Button
                                    data-testid="reschedule-banner-btn"
                                    size="small"
                                    variant="contained"
                                    disableElevation
                                    onClick={() => setBannerRescheduleOpen(true)}
                                    sx={{
                                        fontSize: '11px', py: 0.4, px: 1.25,
                                        borderRadius: '6px', flexShrink: 0,
                                        bgcolor: '#078DEE', color: '#fff',
                                        '&:hover': { bgcolor: '#0671c4' },
                                    }}
                                >
                                    Reschedule
                                </Button>
                                <IconButton
                                    data-testid="reschedule-banner-dismiss"
                                    size="small"
                                    aria-label="Dismiss"
                                    onClick={() => setShowRescheduleBanner(false)}
                                    sx={{ color: '#6e6e73', p: 0.25, flexShrink: 0, '&:hover': { color: '#1d1d1f' } }}
                                >
                                    <Iconify icon="eva:close-fill" width={16} height={16} />
                                </IconButton>
                                <RescheduleDialog
                                    open={bannerRescheduleOpen}
                                    onClose={() => setBannerRescheduleOpen(false)}
                                    onConfirm={(newDate) => {
                                        handleChangeDueDate(checklistUpNext, newDate);
                                        setShowRescheduleBanner(false);
                                    }}
                                    item={checklistUpNext}
                                />
                            </Box>
                        )}
                        {checklistEnabled && !checklistLoading && hiddenTaskTemplate && (
                            <Typography
                                data-testid="hub-task-other-treatment-notice"
                                sx={{ mx: 3, mt: 2, fontSize: '12px', color: '#6e6e73', letterSpacing: '-0.005em' }}
                            >
                                This task&apos;s checklist belongs to {hiddenTaskTemplate}
                            </Typography>
                        )}
                        {checklistEnabled && (
                            <CompletionChecklist
                                initialLoading={checklistLoading}
                                templatesEmpty={checklistEmpty}
                                groups={checklistGroups}
                                upNext={checklistUpNext}
                                showProgress={showProgress}
                                progressDone={progressDone}
                                progressTotal={progressTotal}
                                skippedCount={checklistCounts.skipped}
                                pausedCount={checklistCounts.paused}
                                onResumeChecklist={handleResumeChecklist}
                                resuming={resuming}
                                leadEmail={leadDetail?.email || leadDetail?.lead_contact?.email || ''}
                                practiceId={practiceId}
                                onItemClick={handleItemClick}
                                onUndo={handleUndo}
                                onSkip={handleSkip}
                                onChangeDueDate={handleChangeDueDate}
                                onPauseChecklist={handlePauseChecklist}
                                recentlyCompleted={recentlyCompleted}
                                onUndoRecentlyCompleted={handleAckUndo}
                                onMarkComplete={handleCardMarkComplete}
                                leadId={leadId}
                                onLeadTasksReassigned={() => refreshChecklist({ bustCache: true })}
                                actionPanel={actionPanelItem && (
                                    <ActionPanel
                                        // Keyed by item id — forces a remount on item switch
                                        // so ActionPanel's mount-only template-resolve
                                        // effects (and any panel it routes to, e.g.
                                        // FollowUpPanel) don't keep running against a
                                        // stale, previously-actioned item. See
                                        // AttendedGuidedChecklist.jsx for the full rationale.
                                        key={actionPanelItem?.id ?? 'none'}
                                        item={actionPanelItem}
                                        practiceId={practiceId}
                                        leadId={leadId}
                                        leadDetail={leadDetail}
                                        onClose={handlePanelClose}
                                        onComplete={handlePanelComplete}
                                        onOpenSidebarTab={onOpenSidebarTab}
                                    />
                                )}
                            />
                        )}
                    </Box>
                    {hubFooter}
                </AttendedFullscreenWrapper>
                {pauseDialog}
                {/* Irreversible-close guard for the footer "Complete treatment" button.
                    Cancel = no-op (no API, hub stays open); Confirm = the unchanged
                    handleYes (completeTreatmentAPI → CompleteTreatment POST) → closes. */}
                <CompleteTreatmentConfirmDialog
                    open={completeConfirmOpen}
                    submitting={isLoader}
                    onCancel={() => setCompleteConfirmOpen(false)}
                    onConfirm={() => { handleYes(); }}
                />
            </>
        );
    }

    // LEGACY PATH (no row) — the 4 callers that pass no `row` get the original
    // confirm-only layout (warning box + Cancel/Finish). Unchanged, NOT wrapped in
    // AttendedFullscreenWrapper (it has no checklist/sidebar — a plain confirm).
    return (
        <Dialog
            fullScreen
            TransitionComponent={Transition}
            open={open}
            onClose={handleClose}
            sx={[reminderDialogStyle(), { pt: { xs: 0, sm: 0 }, '.MuiDialog-paperFullScreen': { bgcolor: '#F5F5F7', scrollbarGutter: 'stable' } }]}
        >
            <AppBar sx={[commonDialogHeaderStyle, { position: 'sticky !important', top: 0, alignItems: 'center' }]}>
                <Typography sx={reminderTitleStyle} variant="h6" component="div">
                    Complete treatment
                </Typography>
                <Box sx={cancelBoxStyle}>
                    <Button
                        color="inherit"
                        variant="outlined"
                        style={{ borderColor: '#FFFFFF', color: '#fff' }}
                        onClick={handleClose}
                        disabled={isLoader}
                    >
                        Cancel
                    </Button>
                </Box>
            </AppBar>

            <Container maxWidth="md" sx={{ py: 4, pb: 12 }}>
                <Box sx={{ p: 4, bgcolor: '#ffffff', borderRadius: '12px', width: '100%' }}>
                    <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.022em', mb: 0.5 }}>
                        Mark treatment as complete?
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: '#6e6e73', letterSpacing: '-0.01em', mb: 3 }}>
                        The treatment will be closed and moved to the completed state.
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        gap: 1.25,
                        p: 2,
                        bgcolor: 'rgba(255, 149, 0, 0.08)',
                        borderLeft: '3px solid #ff9500',
                        borderRadius: '8px',
                    }}>
                        <Iconify icon={'bxs:info-circle'} width={18} height={18} sx={{ color: '#ff9500', mt: 0.125, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '13px', color: '#1d1d1f', letterSpacing: '-0.01em', lineHeight: 1.45 }}>
                            This action cannot be undone. Once completed, the treatment is locked from further edits.
                        </Typography>
                    </Box>
                    {currentTreatmentDataGlobal?.treatmentData?.treatment && (
                        <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 1, textAlign: 'center' }}>
                            Treatment: <b>{currentTreatmentDataGlobal.treatmentData.treatment}</b>{currentTreatmentDataGlobal.treatmentData.status ? ` · ${String(currentTreatmentDataGlobal.treatmentData.status).replace(/_/g, ' ')}` : ''}
                        </Typography>
                    )}
                </Box>
            </Container>

            <Box
                sx={{
                    width: '100%',
                    position: 'fixed',
                    left: 0,
                    bottom: 0,
                    pb: 1.5,
                    pt: 1.5,
                    bgcolor: 'rgba(246, 246, 248, 0.82)',
                    backdropFilter: 'saturate(180%) blur(20px)',
                    WebkitBackdropFilter: 'saturate(180%) blur(20px)',
                    borderTop: '0.5px solid rgba(0, 0, 0, 0.10)',
                    zIndex: 99,
                }}
            >
                <Container maxWidth="xl" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                    <Button
                        variant="text"
                        onClick={handleClose}
                        disabled={isLoader}
                        sx={{
                            color: '#1d1d1f',
                            fontSize: '13px',
                            fontWeight: 500,
                            letterSpacing: '-0.01em',
                            textTransform: 'none',
                            px: 2,
                            py: 0.75,
                            borderRadius: '6px',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                        }}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        type="submit"
                        loading={isLoader}
                        loadingPosition="start"
                        onClick={handleYes}
                        disabled={isLoader}
                        sx={{
                            bgcolor: '#00AB55',
                            color: '#ffffff',
                            fontSize: '13px',
                            fontWeight: 600,
                            letterSpacing: '-0.01em',
                            textTransform: 'none',
                            borderRadius: '6px',
                            boxShadow: 'none',
                            px: 2.25,
                            py: 0.75,
                            minWidth: '88px',
                            '&:hover': { bgcolor: '#02934A', boxShadow: 'none' },
                            '&:active': { bgcolor: '#017A3D' },
                            '&.Mui-disabled': { bgcolor: 'rgba(0, 171, 85, 0.35)', color: '#ffffff' },
                        }}
                    >
                        Finish
                    </LoadingButton>
                </Container>
            </Box>
        </Dialog>
    );
};

CompletedTreatmentHub.propTypes = {
    isLoader: PropTypes.bool,
    open: PropTypes.bool,
    handleClose: PropTypes.func,
    handleYes: PropTypes.func,
    row: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    // Called after an in-sidebar edit that changes data the grid behind this
    // dialog renders (currently the treatment start date). The host page wires
    // it to its own table reload.
    onDataChanged: PropTypes.func,
};

CompletedTreatmentHub.defaultProps = {
    isLoader: false,
    open: false,
    handleClose: undefined,
    handleYes: undefined,
    row: undefined,
    practiceId: undefined,
    onDataChanged: undefined,
};

export default CompletedTreatmentHub;
